import React, { Component } from 'react';
import { withRouter } from 'react-router-dom';
import PropTypes from 'prop-types';
import memoize from 'memoize-one';
import { Button, Icon } from 'semantic-ui-react';
import i18next from 'i18next';
import ReactTable, { ReactTableDefaults } from "react-table";
import 'react-table/react-table.css';

import { updateUrlState, updateUrl } from '../../../helpers/url';
import ManagementPanel from './ManagementPanel';
import { TextFilter, SelectionFilter, ChoicesFilter, NumericFilter, DateFilter } from './filters';
import { filteringLogic } from './filters/helper';
import './DataGrid.scss';

class DataGrid extends Component {
    state = { filteredResults: undefined };
    dataGridInstance = null;
    DEFAULTS = {
        pageNumber: 1,
        pageSize: 20,
        resizeMode: false,
        filterMode: true
    };

    calculateFilteredResults = memoize((dataLength, amountOfFiletered) => {
        if (typeof amountOfFiletered === "undefined" && typeof dataLength === "undefined") return 0;
        if (typeof amountOfFiletered === "undefined") return dataLength;
        return amountOfFiletered;
    });

    calculateGridState = memoize((searchQuery, gridColumns = []) => {
        const queryParams = new URLSearchParams(searchQuery);
        const gridState = {};

        gridState.isPaginated = queryParams.get('paginated') === "true";
        gridState.pageNumber = parseInt(queryParams.get('pageNumber') || this.DEFAULTS.pageNumber) - 1;
        gridState.pageSize = parseInt(queryParams.get('pageSize') || this.DEFAULTS.pageSize);

        if (queryParams.has('sortCriteria') && queryParams.has('sortDirection')) {
            gridState.sorted = [{ id: queryParams.get('sortCriteria'), desc: queryParams.get('sortDirection') === "desc" }];
        }

        gridState.resizeMode = queryParams.get('resizeMode') !== null || this.DEFAULTS.resizeMode;
        gridState.filterMode = queryParams.get('filterMode') === "true";

        gridState.filters = {};
        gridColumns.forEach(gridCol => {
            const columnName = gridCol.id || gridCol.accessor;
            if (queryParams.has(columnName)) {
                gridState.filters[columnName] = { content: queryParams.get(columnName) || '' };

                if (gridCol.filterMinMax) gridState.filters[columnName].numeric = true;
                if (gridCol.filterDate) gridState.filters[columnName].filterDate = gridCol.filterDate;
                if (gridCol.filterValues) gridState.filters[columnName].exact = true;
                if (gridCol.filterChoices) {
                    const choices = gridState.filters[columnName].content.split(',');
                    gridState.filters[columnName].content = choices.reduce((obj, key) => ({ ...obj, [key]: true }), {});
                    gridState.filters[columnName].multiChoice = true;
                }
            }
        });

        return gridState;
    });

    constructor(props) {
        super(props);

        this.calculateGridState = this.calculateGridState.bind(this);
        this.renderFilterComponent = this.renderFilterComponent.bind(this);
        this.renderPaginationButton = this.renderPaginationButton.bind(this);
    }

    componentDidMount() {
        const { history, location, paginated } = this.props;
        const queryParams = new URLSearchParams(location.search);

        queryParams.set('paginated', !!paginated);
        queryParams.set('pageNumber', queryParams.get('pageNumber') || this.DEFAULTS.pageNumber);
        queryParams.set('pageSize', queryParams.get('pageSize') || this.DEFAULTS.pageSize);

        if (!queryParams.has('filterMode')) {
            queryParams.set('filterMode', this.DEFAULTS.filterMode);
        }

        updateUrl(history, location, queryParams);
    }

    renderFilterComponent({ filter, column, onChange }) {
        const { location } = this.props;
        const gridState = this.calculateGridState(location.search);

        if (!gridState.filterMode) return null;

        let filterComponent;

        if (column.filterValues) {
            filterComponent = <SelectionFilter filter={filter} filterValues={column.filterValues} onChange={onChange} />;
        } else if (column.filterChoices) {
            filterComponent = <ChoicesFilter filter={filter} filterChoices={column.filterChoices} onChange={onChange} />;
        } else if (column.filterMinMax) {
            filterComponent = <NumericFilter filter={filter} filterMinMax={column.filterMinMax} onChange={onChange} />;
        } else if (column.filterDate) {
            filterComponent = <DateFilter filter={filter} filterDate={column.filterDate} onChange={onChange} />;
        } else {
            filterComponent = <TextFilter filter={filter} onChange={onChange} />;
        }

        return <div className='pi-data-grid-filter'>{filterComponent}</div>;
    }

    renderPaginationButton(direction) {
        const isRtl = i18next.dir() === 'rtl';

        const paginationConfig = {
            next: { labelPosition: 'right', iconName: isRtl ? 'left arrow' : 'right arrow' },
            previous: { labelPosition: 'left', iconName: isRtl ? 'right arrow' : 'left arrow' }
        };

        return ((props) => (
            <Button
                fluid color='grey' basic icon
                labelPosition={paginationConfig[direction].labelPosition}
                className='pi-data-grid-pagination-button'
                {...props} >
                <Icon name={paginationConfig[direction].iconName} />
                {props.children}
            </Button>
        ));
    }

    render() {
        const { history, location, data, columns, extraActions, extraInfo, hidePanel, paginated, ...rest } = this.props;

        const numberOfFilteredResults = this.calculateFilteredResults(data.length, this.state.filteredResults);
        const gridState = this.calculateGridState(location.search, columns);

        if (!gridState.isPaginated) gridState.pageSize = numberOfFilteredResults;

        const dictionary = {
            previousText: i18next.t('dataGrid.previous'),
            nextText: i18next.t('dataGrid.next'),
            loadingText: i18next.t('dataGrid.loading'),
            noDataText: i18next.t('dataGrid.noData'),
            pageText: i18next.t('dataGrid.page'),
            ofText: i18next.t('dataGrid.of'),
            rowsText: i18next.t('dataGrid.rows')
        };

        return (
            <section className='pi-data-grid'>
                {hidePanel ? null :
                    <ManagementPanel
                        data={data}
                        getGridState={() => this.dataGridInstance.getResolvedState()}
                        filteredResults={numberOfFilteredResults}
                        showFilters={gridState.filterMode}
                        resizable={gridState.resizeMode}
                        isPaginated={gridState.isPaginated}
                        actionTriggered={(params) => updateUrlState(history, location, params)}
                        extraActions={extraActions}
                        extraInfo={extraInfo}
                       
                    />
                }
                
                <ReactTable
                    ref={instance => { this.dataGridInstance = instance; }}
                    data={data}
                    columns={columns}
                    column={{
                        ...ReactTableDefaults.column,
                        Filter: this.renderFilterComponent,
                        filterMethod: filteringLogic,
                        headerClassName: 'pi-data-grid-header-column',
                        className: 'pi-data-grid-column',
                    }}
                    defaultFiltered={Object.keys(gridState.filters).map(id => ({ id, value: gridState.filters[id] }))}
                    onFilteredChange={filtered => {
                        const changedFilters = filtered.reduce((obj, { id, value }) => {
                            if (value.multiChoice) return {
                                ...obj,
                                [id]: Object.keys(value.content).filter(choice => value.content[choice]).join(',')
                            };
                            return { ...obj, [id]: value.content };
                        }, {});
                        changedFilters.pageNumber = 1;
                        updateUrlState(history, location, changedFilters);
                        this.setState({ filteredResults: this.dataGridInstance.getResolvedState().sortedData.length });
                    }}
                    sorted={gridState.sorted}
                    onSortedChange={([sortedColumn]) => {
                        updateUrlState(history, location, {
                            sortCriteria: sortedColumn.id,
                            sortDirection: sortedColumn.desc ? "desc" : "asc"
                        });
                    }}
                    resizable={gridState.resizeMode}
                    page={gridState.pageNumber}
                    pageSize={gridState.pageSize}
                    onPageChange={page => { updateUrlState(history, location, { pageNumber: page + 1 }); }}
                    onPageSizeChange={pageSize => { updateUrlState(history, location, { pageSize: pageSize }); }}
                    showPagination={gridState.isPaginated}
                    className='pi-data-grid-table'
                    PreviousComponent={this.renderPaginationButton('previous')}
                    NextComponent={this.renderPaginationButton('next')}
                    {...dictionary}
                    {...rest}
                />
            </section>
        );

    }
}

DataGrid.propTypes = {
    location: PropTypes.object.isRequired,
    history: PropTypes.object.isRequired,
    data: PropTypes.array.isRequired,
    columns: PropTypes.array.isRequired,
    paginated: PropTypes.bool,
    extraActions: PropTypes.node,
    extraInfo: PropTypes.node,
};

export default withRouter(DataGrid);